import { useState } from 'react';
import { SessionMeta, InputMode } from '../lib/types';
import { brand, space, fontFamily } from '../brand/theme';

interface Props {
  sessions: SessionMeta[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: (type: InputMode) => void;
  onDelete: (id: string) => void;
  dirtySessionIds?: Set<string>;
  apiKey?: string;
  onLogout?: () => void;
  onShowAccount?: () => void;
  onShowBatch?: () => void;
}

export default function Sidebar({ sessions, activeId, onSelect, onNew, onDelete, dirtySessionIds = new Set(), apiKey, onLogout, onShowAccount, onShowBatch }: Props) {
  const chats = sessions.filter(s => s.type === 'free_form');
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const timeAgo = (ts: number) => {
    const mins = Math.floor((Date.now() - ts) / 60000);
    if (mins < 1) return 'щойно';
    if (mins < 60) return `${mins}хв`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}год`;
    return `${Math.floor(hrs / 24)}д`;
  };

  const footerBtn: React.CSSProperties = {
    display: 'block', width: '100%', padding: '7px 10px',
    borderRadius: '7px', border: `1px solid ${brand.border}`,
    background: 'transparent', color: brand.creamMuted,
    fontSize: '11px', cursor: 'pointer', fontFamily,
    textAlign: 'left' as const, textDecoration: 'none',
    transition: 'border-color 150ms, color 150ms',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };

  return (
    <div style={{ width: '256px', borderRight: `1px solid ${brand.border}`, display: 'flex', flexDirection: 'column', height: '100%', background: brand.bgCard, flexShrink: 0 }}>

      {/* Header: logo + new chat */}
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${brand.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <a href="https://lapathoniia.top" target="_blank" rel="noopener noreferrer" style={{display:'flex',alignItems:'center',gap:8,minWidth:0,textDecoration:'none'}}>
            <img src="/lapathoniia-logo.png" alt="Lapathoniia" height={26} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '13px', fontWeight: 700, color: brand.cream, letterSpacing: '-0.2px', whiteSpace: 'nowrap' }}>Lapathoniia</span>
          </a>
        </div>
        <button
          onClick={() => onNew('free_form')}
          title="Новий чат"
          style={{ border: `1px solid ${brand.border}`, background: 'transparent', cursor: 'pointer', color: brand.tealLight, padding: '4px 9px', borderRadius: '6px', fontSize: '12px', lineHeight: 1, fontFamily, transition: 'border-color 150ms, background 150ms', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(78,205,196,0.08)'; (e.currentTarget as HTMLElement).style.borderColor = brand.tealLight; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.borderColor = brand.border; }}
        >+ Новий</button>
      </div>

      {/* About — always visible */}
      <div style={{ borderBottom: `1px solid ${brand.border}`, flexShrink: 0, padding: '8px 12px 10px' }}>
        <div style={{ fontSize: '10px', color: brand.creamSubtle, textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 700, marginBottom: 6 }}>Про проєкт</div>
        <div style={{ fontSize: '11px', color: brand.creamMuted, lineHeight: 1.7 }}>
          Перший в Україні сервіс для роботи з українськими LLM, розміщений фізично в Україні.<br />
          <br />
          <a href="https://lapathoniia.top/" target="_blank" rel="noreferrer" style={{ color: brand.tealLight, textDecoration: 'none' }}>🌐 lapathoniia.top</a>
          <br />
          <a href="https://denovo.ua/llm-cloud?utm_source=lapathoniia&utm_medium=referral&utm_term=de-novo" target="_blank" rel="noreferrer" style={{ color: brand.tealLight, textDecoration: 'none' }}>☁️ Хмара De Novo</a>
          <br /><br />
          <span style={{ color: brand.creamSubtle }}>Автори:</span>{' '}
          <a href="https://www.linkedin.com/in/vladyslav-melnyk-640b31134/" target="_blank" rel="noreferrer" style={{ color: brand.cream, textDecoration: 'none' }}>Vladyslav Melnyk</a>
          {', '}
          <a href="https://www.linkedin.com/in/didenkobogdan/" target="_blank" rel="noreferrer" style={{ color: brand.cream, textDecoration: 'none' }}>Bogdan Didenko</a>
        </div>
      </div>

      {/* Chat list */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Fixed label */}
        <div style={{ padding: '6px 12px 2px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '10px', color: brand.creamSubtle, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>Чати</span>
        </div>

        {/* Scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 6px 6px' }}>
          {chats.length === 0 && (
            <div style={{ fontSize: '11px', color: brand.grey400, padding: '12px 6px', textAlign: 'center', opacity: 0.6 }}>
              Немає чатів.{' '}
              <button onClick={() => onNew('free_form')} style={{ background: 'none', border: 'none', color: brand.tealLight, cursor: 'pointer', fontSize: '11px', fontFamily, textDecoration: 'underline', padding: 0 }}>
                Новий
              </button>
            </div>
          )}

          {chats.map(s => {
            const isActive = s.id === activeId;
            const isHovered = s.id === hoveredId;
            const deleteBlocked = dirtySessionIds.has(s.id);
            return (
              <div
                key={s.id}
                onClick={() => onSelect(s.id)}
                onMouseEnter={() => setHoveredId(s.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  padding: '5px 8px', cursor: 'pointer', borderRadius: '7px', marginBottom: '1px',
                  background: isActive ? 'rgba(78,205,196,0.07)' : isHovered ? 'rgba(255,255,255,0.025)' : 'transparent',
                  borderLeft: `2px solid ${isActive ? brand.tealLight : 'transparent'}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4,
                  transition: 'background 120ms, border-color 120ms',
                }}
              >
                <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: isActive ? 500 : 400, color: isActive ? brand.cream : brand.creamMuted }}>
                    {s.mode === 'ocr' ? 'OCR · ' : ''}{s.label}
                  </div>
                  <div style={{ fontSize: '10px', color: brand.creamMuted, marginTop: 1 }}>{timeAgo(s.createdAt)}</div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); onDelete(s.id); }}
                  disabled={deleteBlocked}
                  style={{ border: 'none', background: 'none', cursor: deleteBlocked ? 'not-allowed' : 'pointer', color: brand.grey400, fontSize: '14px', padding: '1px 3px', flexShrink: 0, opacity: isHovered ? (deleteBlocked ? 0.3 : 0.6) : 0, transition: 'opacity 120ms', minWidth: 20, minHeight: 20 }}
                  aria-label={`Видалити ${s.label}`}
                  title={deleteBlocked ? 'Спочатку збережіть або відкиньте OCR-зміни' : 'Видалити'}
                >×</button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 10px', borderTop: `1px solid ${brand.border}`, display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>

        {/* Key status */}
        {onLogout && (
          <button
            onClick={onLogout}
            style={{
              ...footerBtn,
              border: `1px solid ${apiKey ? 'rgba(244,197,66,0.3)' : brand.border}`,
              background: apiKey ? 'rgba(244,197,66,0.05)' : 'transparent',
              color: apiKey ? '#f4c542' : brand.creamMuted,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = apiKey ? 'rgba(244,197,66,0.55)' : brand.tealLight; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = apiKey ? 'rgba(244,197,66,0.3)' : brand.border; }}
          >
            {apiKey ? (
              <>
                <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', flexShrink: 0 }}>✓ Ключ</span>
                <span style={{ opacity: 0.55, fontFamily: 'monospace', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{apiKey.slice(0, 8)}···</span>
              </>
            ) : (
              <span>🔑 Демо-режим · змінити ключ</span>
            )}
          </button>
        )}

        {/* Batch mode */}
        {onShowBatch && (
          <button
            onClick={onShowBatch}
            style={{
              ...footerBtn,
              border: `1px solid rgba(78,205,196,0.25)`,
              color: brand.tealLight,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = brand.tealLight; (e.currentTarget as HTMLElement).style.background = 'rgba(78,205,196,0.07)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(78,205,196,0.25)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <span>⚡ Пакетний режим</span>
            <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '8px', background: 'rgba(78,205,196,0.15)', border: `1px solid rgba(78,205,196,0.3)` }}>-40%</span>
          </button>
        )}

        {/* Docs */}
        <a
          href="https://docs.lapathoniia.top"
          target="_blank" rel="noopener noreferrer"
          style={footerBtn}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = brand.tealLight; (e.currentTarget as HTMLElement).style.color = brand.cream; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = brand.border; (e.currentTarget as HTMLElement).style.color = brand.creamMuted; }}
        >
          📚 Документація ↗
        </a>

        {/* Dashboard */}
        <a
          href="https://lapathoniia.top/dashboard"
          target="_blank" rel="noopener noreferrer"
          style={footerBtn}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = brand.tealLight; (e.currentTarget as HTMLElement).style.color = brand.cream; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = brand.border; (e.currentTarget as HTMLElement).style.color = brand.creamMuted; }}
        >
          📊 Особистий кабінет ↗
        </a>

        {/* Report issue */}
        <a
          href="https://github.com/Lapathoniia/feedback/issues/new"
          target="_blank" rel="noopener noreferrer"
          style={footerBtn}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = brand.tealLight; (e.currentTarget as HTMLElement).style.color = brand.cream; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = brand.border; (e.currentTarget as HTMLElement).style.color = brand.creamMuted; }}
        >
          🐛 Повідомити про проблему
        </a>

        {/* Disclaimer */}
        <div style={{ fontSize: '9.5px', color: brand.creamSubtle, lineHeight: 1.4, padding: '2px 2px 0', opacity: 0.7 }}>
          🔒 Історія прив'язана до браузера.
        </div>
      </div>
    </div>
  );
}
